import type { FinanceCategoryKind } from './types';

/** Transaction fields a CSV column can map to. */
export const IMPORT_FIELDS = [
  'date',
  'category',
  'account',
  'toAccount',
  'currency',
  'amount',
  'notes',
] as const;
export type ImportField = (typeof IMPORT_FIELDS)[number];

export const IMPORT_FIELD_LABELS: Record<ImportField, string> = {
  date: 'Date',
  category: 'Category',
  account: 'Account',
  toAccount: 'To account',
  currency: 'Currency',
  amount: 'Amount',
  notes: 'Notes',
};

/** Fields that must be mapped before an import can run. */
export const REQUIRED_IMPORT_FIELDS: ImportField[] = ['date', 'currency', 'amount'];

/** Field → source column index. */
export type ColumnMapping = Partial<Record<ImportField, number>>;

/**
 * Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped `""`, commas
 * inside quotes, and CRLF/LF newlines. The first non-empty line is the header.
 */
export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  // Google Sheets / Excel copy-paste is tab-separated; CSV exports are comma-separated.
  // Sniff the first line so both "just paste" and file imports work.
  const firstLine = text.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  const records: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let started = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    records.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    started = true;
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      pushField();
    } else if (ch === '\r') {
      // swallow; \n handles the row break
    } else if (ch === '\n') {
      pushRow();
    } else {
      field += ch;
    }
  }
  if (started && (field !== '' || row.length > 0)) {
    pushRow();
  }

  // Drop fully-empty trailing rows.
  const nonEmpty = records.filter((r) => r.some((cell) => cell.trim() !== ''));
  const headers = nonEmpty.length > 0 ? nonEmpty[0]!.map((h) => h.trim()) : [];
  return { headers, rows: nonEmpty.slice(1) };
}

function normalizeHeader(header: string): string {
  return header.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const HEADER_ALIASES: Record<ImportField, string[]> = {
  date: ['date', 'transactiondate', 'day'],
  category: ['category', 'cat', 'categories'],
  account: ['account', 'fromaccount', 'acct', 'source'],
  toAccount: ['toaccount', 'destination', 'destaccount', 'transferto'],
  currency: ['currency', 'ccy', 'curr'],
  amount: ['amount', 'value', 'sum', 'total'],
  notes: ['notes', 'note', 'description', 'memo', 'details'],
};

/** Best-effort guess of which column feeds each field, by header name. */
export function autoMapColumns(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const normalized = headers.map(normalizeHeader);
  for (const field of IMPORT_FIELDS) {
    const aliases = HEADER_ALIASES[field];
    const index = normalized.findIndex((h) => aliases.includes(h));
    if (index !== -1) {
      mapping[field] = index;
    }
  }
  return mapping;
}

function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Parse a date cell to `YYYY-MM-DD`, or null if unparseable.
 * Accepts ISO (`YYYY-M-D`, `YYYY/M/D`) and US `M/D/YYYY` (Google Sheets en-US default).
 */
export function parseDateToIso(value: string): string | null {
  const v = value.trim();
  if (v === '') return null;

  let m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (m) {
    const [, y, mo, d] = m.map(Number);
    return isRealDate(y!, mo!, d!) ? iso(y!, mo!, d!) : null;
  }

  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(v);
  if (m) {
    const [, mo, d, y] = m.map(Number);
    return isRealDate(y!, mo!, d!) ? iso(y!, mo!, d!) : null;
  }

  return null;
}

/**
 * Parse an amount cell to an unsigned number, or null if unparseable / non-positive.
 * Strips currency symbols and thousands separators; direction comes from the category
 * kind, so the sign is discarded (parentheses/negatives import as positive amounts).
 */
export function parseAmount(value: string): number | null {
  const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Math.abs(Number(cleaned));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export type ImportLookups = {
  /** Uppercased currency codes that exist in Master Data. */
  currencyCodes: Set<string>;
  /** Lowercased account name → id. */
  accountsByName: Map<string, number>;
  /** Lowercased `Parent - Child` label → category. */
  categoryByLabel: Map<string, { id: number; kind: FinanceCategoryKind }>;
  /** Lowercased account names the user chose to leave blank (treated as intentional). */
  blankAccounts?: Set<string>;
  /** Lowercased category labels the user chose to leave blank. */
  blankCategories?: Set<string>;
};

export type ResolvedRow = {
  index: number;
  date: string | null;
  currencyCode: string | null;
  amount: number | null;
  notes: string | null;
  accountName: string;
  toAccountName: string;
  categoryLabel: string;
  accountId: number | null;
  toAccountId: number | null;
  categoryId: number | null;
  kind: FinanceCategoryKind | null;
  /** Non-empty raw name that matched neither Master Data nor a blank choice. */
  accountUnresolved: boolean;
  toAccountUnresolved: boolean;
  categoryUnresolved: boolean;
  errors: string[];
};

function cell(row: string[], mapping: ColumnMapping, field: ImportField): string {
  const index = mapping[field];
  if (index == null) return '';
  return (row[index] ?? '').trim();
}

/** Resolve one CSV row against Master Data + the user's name-mapping choices. */
function resolveRow(
  row: string[],
  mapping: ColumnMapping,
  lookups: ImportLookups,
  index: number,
): ResolvedRow {
  const errors: string[] = [];

  const dateRaw = cell(row, mapping, 'date');
  const date = parseDateToIso(dateRaw);
  if (dateRaw === '') errors.push('Missing date');
  else if (date == null) errors.push(`Invalid date "${dateRaw}"`);

  const currencyRaw = cell(row, mapping, 'currency').toUpperCase();
  let currencyCode: string | null = null;
  if (currencyRaw === '') {
    errors.push('Missing currency');
  } else if (!lookups.currencyCodes.has(currencyRaw)) {
    errors.push(`Unknown currency "${currencyRaw}"`);
  } else {
    currencyCode = currencyRaw;
  }

  const amountRaw = cell(row, mapping, 'amount');
  const amount = parseAmount(amountRaw);
  if (amountRaw === '') errors.push('Missing amount');
  else if (amount == null) errors.push(`Invalid amount "${amountRaw}"`);

  const accountName = cell(row, mapping, 'account');
  let accountId: number | null = null;
  let accountUnresolved = false;
  if (accountName !== '') {
    const key = accountName.toLowerCase();
    const matched = lookups.accountsByName.get(key);
    if (matched != null) accountId = matched;
    else if (!lookups.blankAccounts?.has(key)) accountUnresolved = true;
  }

  const categoryLabel = cell(row, mapping, 'category');
  let categoryId: number | null = null;
  let kind: FinanceCategoryKind | null = null;
  let categoryUnresolved = false;
  if (categoryLabel !== '') {
    const key = categoryLabel.toLowerCase();
    const matched = lookups.categoryByLabel.get(key);
    if (matched) {
      categoryId = matched.id;
      kind = matched.kind;
    } else if (!lookups.blankCategories?.has(key)) {
      categoryUnresolved = true;
    }
  }

  const toAccountName = cell(row, mapping, 'toAccount');
  let toAccountId: number | null = null;
  let toAccountUnresolved = false;
  if (toAccountName !== '') {
    const key = toAccountName.toLowerCase();
    const matched = lookups.accountsByName.get(key);
    if (matched != null) toAccountId = matched;
    else if (!lookups.blankAccounts?.has(key)) toAccountUnresolved = true;
  }
  if (toAccountId != null && accountId != null && toAccountId === accountId) {
    errors.push('Transfer targets the same account');
  }

  if (accountUnresolved) errors.push(`Unmatched account "${accountName}"`);
  if (toAccountUnresolved) errors.push(`Unmatched to-account "${toAccountName}"`);
  if (categoryUnresolved) errors.push(`Unmatched category "${categoryLabel}"`);

  return {
    index,
    date,
    currencyCode,
    amount,
    notes: cell(row, mapping, 'notes') || null,
    accountName,
    toAccountName,
    categoryLabel,
    accountId,
    toAccountId,
    categoryId,
    kind,
    accountUnresolved,
    toAccountUnresolved,
    categoryUnresolved,
    errors,
  };
}

export function buildResolvedRows(
  rows: string[][],
  mapping: ColumnMapping,
  lookups: ImportLookups,
): ResolvedRow[] {
  return rows.map((row, i) => resolveRow(row, mapping, lookups, i));
}

/** Distinct raw names (case-insensitive, first spelling kept) still needing a decision. */
export function distinctUnresolved(rows: ResolvedRow[]): {
  accounts: string[];
  categories: string[];
} {
  const accounts = new Map<string, string>();
  const categories = new Map<string, string>();
  for (const row of rows) {
    if (row.accountUnresolved) accounts.set(row.accountName.toLowerCase(), row.accountName);
    if (row.toAccountUnresolved) accounts.set(row.toAccountName.toLowerCase(), row.toAccountName);
    if (row.categoryUnresolved) categories.set(row.categoryLabel.toLowerCase(), row.categoryLabel);
  }
  return { accounts: [...accounts.values()], categories: [...categories.values()] };
}

/** Placeholder id for a category the user chose to create; replaced with the real id at import. */
export const CREATE_CATEGORY_SENTINEL_ID = -1;

/** `blank` | `create` (categories only) | a stringified existing entity id. */
export type NameAction = string;

export type NameActions = {
  /** lower-cased account name → action (default `blank`). */
  accounts: Map<string, NameAction>;
  /** lower-cased category label → action (default `create`). */
  categories: Map<string, NameAction>;
};

/**
 * Fold the user's per-name decisions into the Master-Data lookups, applying the
 * defaults (unmatched account → leave blank, unmatched category → create). Category
 * `create` choices get {@link CREATE_CATEGORY_SENTINEL_ID} so they count as resolved
 * during review; the real id is substituted at import time once they're created.
 */
export function effectiveLookups(
  base: ImportLookups,
  unresolved: { accounts: string[]; categories: string[] },
  actions: NameActions,
  categoryKindById: Map<number, FinanceCategoryKind>,
): ImportLookups {
  const accountsByName = new Map(base.accountsByName);
  const categoryByLabel = new Map(base.categoryByLabel);
  const blankAccounts = new Set<string>();
  const blankCategories = new Set<string>();

  for (const name of unresolved.accounts) {
    const key = name.toLowerCase();
    const action = actions.accounts.get(key) ?? 'blank';
    if (/^\d+$/.test(action)) accountsByName.set(key, Number(action));
    else blankAccounts.add(key);
  }

  for (const label of unresolved.categories) {
    const key = label.toLowerCase();
    const action = actions.categories.get(key) ?? 'create';
    if (action === 'blank') {
      blankCategories.add(key);
    } else if (/^\d+$/.test(action)) {
      const id = Number(action);
      categoryByLabel.set(key, { id, kind: categoryKindById.get(id) ?? 'expense' });
    } else {
      categoryByLabel.set(key, { id: CREATE_CATEGORY_SENTINEL_ID, kind: 'expense' });
    }
  }

  return {
    currencyCodes: base.currencyCodes,
    accountsByName,
    categoryByLabel,
    blankAccounts,
    blankCategories,
  };
}

/** Split a `Parent - Child` label into trimmed segments (single-segment = top-level). */
export function splitCategoryLabel(label: string): { parent: string; child: string | null } {
  const idx = label.indexOf(' - ');
  if (idx === -1) return { parent: label.trim(), child: null };
  return { parent: label.slice(0, idx).trim(), child: label.slice(idx + 3).trim() };
}

export type ExistingCategory = { id: number; name: string; parentId: number | null };

export type CategoryImportPlan = {
  /** Top-level groups to create, in first-seen order. */
  newParents: string[];
  /** Subcategories to create, each under a (new or existing) parent name. */
  newChildren: { parent: string; name: string }[];
  /** Non-empty lines parsed. */
  parsed: number;
  /** Lines whose parent + child already exist (nothing to create). */
  duplicates: number;
};

/**
 * Plan a paste-in category import: one `Parent - Child` (or bare `Parent`) per
 * line, dash-delimited like the spreadsheet. Compares against existing
 * categories (case-insensitive) so nothing is duplicated, and preserves order.
 */
export function planCategoryImport(
  text: string,
  existing: ExistingCategory[],
): CategoryImportPlan {
  const parentNameById = new Map<number, string>();
  for (const c of existing) {
    if (c.parentId == null) parentNameById.set(c.id, c.name);
  }

  const existingParents = new Set<string>();
  const existingChildren = new Set<string>();
  for (const c of existing) {
    if (c.parentId == null) {
      existingParents.add(c.name.toLowerCase());
    } else {
      const parentName = parentNameById.get(c.parentId);
      if (parentName) existingChildren.add(`${parentName.toLowerCase()}|${c.name.toLowerCase()}`);
    }
  }

  const newParents: string[] = [];
  const queuedParents = new Set<string>();
  const newChildren: { parent: string; name: string }[] = [];
  const queuedChildren = new Set<string>();
  let parsed = 0;
  let duplicates = 0;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    parsed += 1;

    const { parent, child } = splitCategoryLabel(line);
    if (!parent) continue;
    const parentKey = parent.toLowerCase();

    const parentKnown = existingParents.has(parentKey) || queuedParents.has(parentKey);
    let lineCreates = false;
    if (!parentKnown) {
      newParents.push(parent);
      queuedParents.add(parentKey);
      lineCreates = true;
    }

    if (child) {
      const childKey = `${parentKey}|${child.toLowerCase()}`;
      if (!existingChildren.has(childKey) && !queuedChildren.has(childKey)) {
        newChildren.push({ parent, name: child });
        queuedChildren.add(childKey);
        lineCreates = true;
      }
    }

    if (!lineCreates) duplicates += 1;
  }

  return { newParents, newChildren, parsed, duplicates };
}

// --- Budget import (paste from Google Sheets) ---

export const BUDGET_IMPORT_FIELDS = ['month', 'category', 'min', 'max', 'notes'] as const;
export type BudgetImportField = (typeof BUDGET_IMPORT_FIELDS)[number];
export type BudgetColumnMapping = Partial<Record<BudgetImportField, number>>;

const BUDGET_HEADER_ALIASES: Record<BudgetImportField, string[]> = {
  month: ['month', 'period'],
  category: ['category', 'cat'],
  min: ['minusd', 'min', 'minimum', 'minbudget', 'low'],
  max: ['maxusd', 'max', 'maximum', 'maxbudget', 'budget', 'high'],
  notes: ['notes', 'note', 'memo', 'description'],
};

export function autoMapBudgetColumns(headers: string[]): BudgetColumnMapping {
  const mapping: BudgetColumnMapping = {};
  const normalized = headers.map(normalizeHeader);
  for (const field of BUDGET_IMPORT_FIELDS) {
    const index = normalized.findIndex((h) => BUDGET_HEADER_ALIASES[field].includes(h));
    if (index !== -1) mapping[field] = index;
  }
  return mapping;
}

/** Parse a budget month cell (`YYYY-M`, `YYYY-MM`, or `YYYY-MM-DD`) to `YYYY-MM`. */
export function parseBudgetMonth(value: string): string | null {
  const v = value.trim();
  let m = /^(\d{4})[-/](\d{1,2})$/.exec(v);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return `${year}-${String(month).padStart(2, '0')}`;
  }
  m = /^(\d{4})[-/](\d{1,2})[-/]\d{1,2}$/.exec(v);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return `${year}-${String(month).padStart(2, '0')}`;
  }
  return null;
}

/** Parse a budget amount (strips symbols/thousands separators); empty → 0; negative → null. */
export function parseBudgetAmount(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const cleaned = trimmed.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export type ResolvedBudgetRow = {
  index: number;
  month: string | null;
  categoryLabel: string;
  categoryId: number | null;
  minUsd: number;
  maxUsd: number;
  notes: string | null;
  errors: string[];
};

/**
 * Resolve pasted budget rows against existing categories (matched by `Parent - Child`
 * label, case-insensitive). Unknown categories are marked with
 * {@link CREATE_CATEGORY_SENTINEL_ID} so they can be created during import. The
 * `High level category` column (if present) is ignored.
 */
export function buildResolvedBudgetRows(
  text: string,
  categoryByLabel: Map<string, number>,
): { mapping: BudgetColumnMapping; rows: ResolvedBudgetRow[] } {
  const { headers, rows } = parseCsv(text);
  const mapping = autoMapBudgetColumns(headers);

  const resolved = rows.map((row, index) => {
    const errors: string[] = [];
    const get = (field: BudgetImportField) => {
      const i = mapping[field];
      return i == null ? '' : (row[i] ?? '').trim();
    };

    const monthRaw = get('month');
    const month = parseBudgetMonth(monthRaw);
    if (monthRaw === '') errors.push('Missing month');
    else if (month == null) errors.push(`Invalid month "${monthRaw}"`);

    const categoryLabel = get('category');
    let categoryId: number | null = null;
    if (categoryLabel === '') {
      errors.push('Missing category');
    } else {
      categoryId =
        categoryByLabel.get(categoryLabel.toLowerCase()) ?? CREATE_CATEGORY_SENTINEL_ID;
    }

    const min = parseBudgetAmount(get('min'));
    const max = parseBudgetAmount(get('max'));
    if (min == null) errors.push('Invalid min');
    if (max == null) errors.push('Invalid max');
    const minUsd = min ?? 0;
    const maxUsd = max ?? 0;
    if (min != null && max != null) {
      if (minUsd === 0 && maxUsd === 0) errors.push('No budget amount');
      else if (maxUsd < minUsd) errors.push('Max is below min');
    }

    return {
      index,
      month,
      categoryLabel,
      categoryId,
      minUsd,
      maxUsd,
      notes: get('notes') || null,
      errors,
    };
  });

  return { mapping, rows: resolved };
}
